import React from "react";
import { useLocation } from "react-router-dom";
import { Menu, Target, X, WifiOff } from "lucide-react";
import { useSession } from "../context/SessionContext";
import { useOnlineStatus } from "../lib/useOnlineStatus";

const PAGE_TITLES = {
  overview: { title: "Overview", eyebrow: "Multi-Branch Health" },
  sales: { title: "Sales", eyebrow: "Revenue, Categories, Footfall" },
  inventory: { title: "Inventory", eyebrow: "Stock, Expiry & Aging" },
  procurement: { title: "Procurement", eyebrow: "Vendors, Reorders & Costs" },
  hr: { title: "HR & Staffing", eyebrow: "Roster & Coverage" },
  analytics: { title: "Analytics", eyebrow: "Chain Intelligence & Forecasts" },
};

export const TopBar = ({ onMenu }) => {
  const { pathname } = useLocation();
  const { session, bootstrap, focusBranch, clearFocus } = useSession();
  const online = useOnlineStatus();
  const seg = pathname.split("/")[2] || "overview";
  const meta = PAGE_TITLES[seg] || PAGE_TITLES.overview;

  const scopeLabel = (() => {
    if (!session || !bootstrap) return "";
    if (session.role === "store_manager" && session.branch_id) {
      const b = bootstrap.branches.find((x) => x.id === session.branch_id);
      return b ? `${b.name} · ${b.region} Chennai` : "Single branch";
    }
    if (focusBranch) {
      return `Focused: ${focusBranch.name} · ${focusBranch.region} Chennai`;
    }
    if (session.role === "regional_manager" && session.region) {
      const count = bootstrap.branches.filter((b) => b.region === session.region).length;
      return `${session.region} Chennai · ${count} branches`;
    }
    return `All ${bootstrap.branches.length} branches`;
  })();

  const today = bootstrap?.today
    ? new Date(bootstrap.today).toLocaleDateString("en-IN", {
        weekday: "long", day: "numeric", month: "long", year: "numeric",
      })
    : "";

  return (
    <div
      className="min-h-16 flex items-center justify-between gap-3 px-4 sm:px-6 lg:px-8 py-3 bg-white sticky top-0 z-20"
      style={{ borderBottom: "1px solid var(--ang-border)" }}
      data-testid="topbar"
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <button
          type="button"
          onClick={onMenu}
          data-testid="btn-open-sidebar"
          aria-label="Open menu"
          className="lg:hidden shrink-0 w-9 h-9 flex items-center justify-center rounded-xl bg-[#F5F6FA] hover:bg-[#EEF0F5] transition-colors"
        >
          <Menu size={18} strokeWidth={2} />
        </button>

        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-ang-muted truncate">{meta.eyebrow}</div>
              <h1
                className="font-extrabold text-xl sm:text-2xl md:text-[26px] leading-tight mt-0.5 truncate"
                style={{ letterSpacing: "-0.02em" }}
                data-testid="page-title"
              >
                {meta.title}
              </h1>
            </div>
            {scopeLabel && (
              <div className="hidden md:flex items-center gap-2 min-w-0 pl-4"
                style={{ borderLeft: "1px solid var(--ang-border)" }}
              >
                <span className="text-[10px] font-semibold uppercase tracking-widest text-ang-muted">Scope</span>
                <span className="text-sm font-semibold truncate" data-testid="scope-label">{scopeLabel}</span>
              </div>
            )}
          </div>
          {scopeLabel && (
            <div className="md:hidden text-[11px] text-ang-muted mt-0.5 truncate" data-testid="scope-label-mobile">
              <span className="uppercase tracking-widest mr-1 font-semibold">Scope</span>
              {scopeLabel}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 sm:gap-3 shrink-0">
        {focusBranch && (
          <button
            type="button"
            onClick={clearFocus}
            data-testid="btn-clear-focus"
            title="Clear branch focus"
            className="hidden sm:inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] uppercase tracking-widest font-bold transition-colors"
            style={{ color: "#7C6FF0", background: "rgba(124,111,240,0.12)" }}
          >
            <Target size={11} strokeWidth={2.4} />
            <span className="max-w-[120px] truncate">{focusBranch.name}</span>
            <X size={11} strokeWidth={2.4} />
          </button>
        )}
        <div className="hidden xl:block text-xs font-medium text-ang-muted tabular-nums">{today}</div>
        <span
          data-testid="online-status"
          title={online ? "Online" : "Offline — serving cached data"}
          className={`inline-flex items-center rounded-full px-2.5 py-1 text-[10px] uppercase tracking-widest font-bold ${online ? "ang-live-dot" : ""}`}
          style={{
            color: online ? "#059851" : "#B42318",
            background: online ? "#ECFDF3" : "#FEF3F2",
          }}
        >
          {!online && <WifiOff size={11} strokeWidth={2.4} className="mr-1.5" />}
          <span className="hidden sm:inline">{online ? "Live" : "Offline"}</span>
        </span>
      </div>
    </div>
  );
};

export default TopBar;
